import React, { useMemo } from 'react';

import { IsoCountryCode2 } from '@rbilabs/intl';
import { useIntl } from 'react-intl';

import { INutritionInfo } from '@rbi-ctg/menu';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useFormatJoules } from 'hooks/use-format-joules';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  ItemNutritionInfoToHide,
  defaultItemNutritionInfoToHide,
} from 'state/launchdarkly/variations';
import { TLocalizationKey } from 'types/i18n';
import { RBIBrand, brand, getCountry } from 'utils/environment';
import { getUnitForNutrient } from 'utils/menu/units';

import { ProductSize } from '../product-size';
import { StyledAdditionalInfo } from '../styled/additional-information';
import { StyledAllergens } from '../styled/allergens';
import { ContentWrapper } from '../styled/content-wrapper';
import { ItemName } from '../styled/item-name';
import { Label } from '../styled/label';
import { Nutrient } from '../styled/nutrient';
import { Picture } from '../styled/picture';
import { TableHeader } from '../styled/table-header';
import { Nutrition } from '../v2/nutrition';

import { IItemNutritionProps } from './types';

const nutritionIdsEu = [
  'weight',
  'calories',
  'carbohydrates',
  'sugar',
  'fat',
  'saturatedFat',
  'proteins',
  'salt',
] as const satisfies ReadonlyArray<keyof INutritionInfo>;

const nutritionIdsUs = [
  'weight',
  'calories',
  'fat',
  'saturatedFat',
  'transFat',
  'cholesterol',
  'sodium',
  'carbohydrates',
  'fiber',
  'sugar',
  'proteins',
] as const satisfies ReadonlyArray<keyof INutritionInfo>;

type TNutritionKeyEu = (typeof nutritionIdsEu)[number];
type TNutritionKeyUs = (typeof nutritionIdsUs)[number];
export type TNutritionKey = TNutritionKeyEu | TNutritionKeyUs;

const prefixedKeyMapEu: Record<TNutritionKeyEu, TLocalizationKey> = {
  weight: 'eu-weight',
  calories: 'eu-calories',
  carbohydrates: 'eu-carbohydrates',
  sugar: 'eu-sugar',
  fat: 'eu-fat',
  saturatedFat: 'eu-saturatedFat',
  proteins: 'eu-proteins',
  salt: 'eu-salt',
};

const prefixedKeyMapUs: Record<TNutritionKeyUs, TLocalizationKey> = {
  weight: 'us-weight',
  calories: 'us-calories',
  fat: 'us-fat',
  saturatedFat: 'us-saturatedFat',
  transFat: 'us-transFat',
  cholesterol: 'us-cholesterol',
  sodium: 'us-sodium',
  carbohydrates: 'us-carbohydrates',
  fiber: 'us-fiber',
  sugar: 'us-sugar',
  proteins: 'us-proteins',
};

export const ItemNutrition: React.FC<IItemNutritionProps> = ({
  allergenGroups,
  itemImage,
  itemName,
  nutritionInfo,
  productSize,
  enableFeatureNutritionIntegration,
  enableStaticMenuAllergens,
  additionalItemInformation,
}) => {
  const { formatNumber, formatMessage } = useIntl();
  const formatCalories = useFormatCalories();
  const formatJoules = useFormatJoules();

  const enableEUNutritionDisplay = useFlag(LaunchDarklyFlag.ENABLE_EU_NUTRITION_DISPLAY);
  const itemsFromNutritionInfoToHide =
    useFlag<ItemNutritionInfoToHide>(LaunchDarklyFlag.HIDE_ITEMS_FROM_NUTRITION_INFO) ||
    defaultItemNutritionInfoToHide;
  const enableGermanLegalRequirement = useFlag(LaunchDarklyFlag.ENABLE_GERMAN_LEGAL_REQUIREMENT);
  const isGermany = getCountry().toLowerCase() === IsoCountryCode2.DE.toLowerCase();

  const isBKDEAndGermanLegalRequirement =
    brand() === RBIBrand.BK && isGermany && enableGermanLegalRequirement;

  const nutritionIds = useMemo(
    () => (enableEUNutritionDisplay ? nutritionIdsEu : nutritionIdsUs),
    [enableEUNutritionDisplay]
  );

  const perServing = useMemo(() => {
    return (
      <>
        <TableHeader data-testid="per-serving-header">
          {formatMessage({ id: 'perServing' })}
        </TableHeader>
        {nutritionIds.map(key => {
          const value = nutritionInfo[key];

          if ((key === 'weight' && value === 0) || itemsFromNutritionInfoToHide[key]) {
            return null;
          }

          if (key === 'calories' && enableEUNutritionDisplay) {
            return (
              <Nutrient key={key}>
                <Label>{formatMessage({ id: prefixedKeyMapEu[key] })}</Label>
                <span>
                  {formatMessage(
                    { id: 'euNutritionValues' },
                    {
                      calories: formatCalories(value),
                      joules: formatJoules(value),
                    }
                  )}
                </span>
              </Nutrient>
            );
          }

          return (
            <Nutrient key={key}>
              <Label>
                {formatMessage({
                  id: enableEUNutritionDisplay
                    ? prefixedKeyMapEu[key as TNutritionKeyEu]
                    : prefixedKeyMapUs[key as TNutritionKeyUs],
                })}
              </Label>
              <span>
                {`${formatNumber(value, {
                  maximumFractionDigits: 1,
                })} ${getUnitForNutrient(key)}`}
              </span>
            </Nutrient>
          );
        })}
      </>
    );
  }, [
    formatMessage,
    nutritionIds,
    nutritionInfo,
    itemsFromNutritionInfoToHide,
    enableEUNutritionDisplay,
    formatNumber,
    formatCalories,
    formatJoules,
  ]);

  const perUnit = useMemo(() => {
    if (
      nutritionInfo.weight === 0 ||
      nutritionInfo.weight === undefined ||
      nutritionInfo.weight === null ||
      !enableEUNutritionDisplay
    ) {
      return null;
    }

    const createInnerContent = (key: TNutritionKey) => {
      const weightRatio = nutritionInfo.weight / 100;

      if (key === 'weight') {
        return null;
      }

      const value = nutritionInfo[key] / weightRatio;

      if (key === 'calories' && enableEUNutritionDisplay) {
        return (
          <Nutrient key={key}>
            <Label>{formatMessage({ id: `eu-${key}` })}</Label>
            <span>
              {formatMessage(
                { id: 'euNutritionValues' },
                {
                  calories: formatCalories(value),
                  joules: formatJoules(value),
                }
              )}
            </span>
          </Nutrient>
        );
      }

      return (
        <Nutrient key={key}>
          <Label>
            {formatMessage({ id: enableEUNutritionDisplay ? `eu-${key}` : `us-${key}` })}
          </Label>
          <span>
            {`${formatNumber(value, {
              maximumFractionDigits: 1,
            })} ${getUnitForNutrient(key)}`}
          </span>
        </Nutrient>
      );
    };

    const content = nutritionIds.map(key => {
      if (itemsFromNutritionInfoToHide[key]) {
        return null;
      }

      return createInnerContent(key);
    });

    return (
      <>
        <TableHeader data-testid="per-mass-header">
          {formatMessage({ id: 'perOneHundredGOrMl' })}
        </TableHeader>
        {content}
      </>
    );
  }, [
    nutritionInfo,
    enableEUNutritionDisplay,
    nutritionIds,
    itemsFromNutritionInfoToHide,
    formatMessage,
    formatNumber,
    formatCalories,
    formatJoules,
  ]);

  const allergenInformation = (
    <>
      {allergenGroups && allergenGroups.length > 0 ? (
        <>
          <TableHeader data-testid="allergens-header">
            {formatMessage({ id: 'allergenInformation' })}
          </TableHeader>
          <StyledAllergens allergenGroups={allergenGroups || []} />
        </>
      ) : null}
    </>
  );

  const additionalInfoSection = (
    <>
      <TableHeader
        data-testid="additional-information-header"
        $isBKDEAndGermanLegalRequirement={isBKDEAndGermanLegalRequirement}
      >
        {formatMessage({ id: 'additionalInfoTitle' })}
      </TableHeader>
      <StyledAdditionalInfo
        additionalInfo={additionalItemInformation}
        productSize={productSize}
        enableStaticMenuAllergens={enableStaticMenuAllergens}
        isBKDEAndGermanLegalRequirement={isBKDEAndGermanLegalRequirement}
        allergenInformation={allergenInformation}
      />
    </>
  );

  return (
    <>
      <Picture image={itemImage} alt="" objectFitContain />
      <ContentWrapper>
        <ItemName data-testid="product-name">{itemName}</ItemName>
        {!isBKDEAndGermanLegalRequirement && <ProductSize size={productSize}></ProductSize>}
        {isBKDEAndGermanLegalRequirement && additionalItemInformation
          ? additionalInfoSection
          : null}
        {enableFeatureNutritionIntegration ? (
          <Nutrition nutritionInfo={nutritionInfo} />
        ) : (
          <>
            {perServing}
            {perUnit}
          </>
        )}
        {!isBKDEAndGermanLegalRequirement && additionalItemInformation
          ? additionalInfoSection
          : null}
        {!isBKDEAndGermanLegalRequirement && enableStaticMenuAllergens && allergenInformation}
      </ContentWrapper>
    </>
  );
};
